(function () {
  'use strict';

  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  // Signal to the first-party analytics snippet that the semantic topture:*
  // cart events (add_to_cart / view_cart / remove_from_cart) are wired and
  // live here, so it uses them as the source of truth and disables its
  // fallback cart-diff inference (prevents double-counted adds/removes). Set
  // synchronously at init — before any cart event can fire. Only reached when
  // the drawer is present (i.e. the events are actually dispatched).
  window.Theme = window.Theme || {};
  window.Theme.toptureCartEvents = true;

  var panel = drawer.querySelector('.cart-drawer__panel');
  var lastFocusedBeforeOpen = null;   // WCAG 2.4.3 focus return target

  var scrollLockY = 0;
  var lockCount = 0;

  function lockScroll() {
    if (lockCount++) return;
    scrollLockY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = (-scrollLockY) + 'px';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    if (--lockCount > 0) return;
    if (lockCount < 0) lockCount = 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    var prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, scrollLockY);
    document.documentElement.style.scrollBehavior = prev;
  }

  // Make the panel visible + lock the background. IDEMPOTENT: a second reveal
  // while already open must NOT lockScroll() again — that double-increments the
  // refcount and a single close then leaves body{position:fixed} stuck (page
  // scroll-locked). This matters because openDrawerAfterAdd() defers its reveal
  // until AFTER an async refresh, so a concurrent openDrawer() (e.g. bundle-cart
  // calling ToptureCartDrawer.open() on the same add) can win the race and open
  // first; when the deferred reveal finally runs, is-open is already set.
  // Hides the floating bar BEFORE paint — the pre-refresh DOM may still show it
  // from a previous session; reconcile() reveals it once the content settles.
  function revealDrawer() {
    if (drawer.classList.contains('is-open')) return;
    var openBar = drawer.querySelector('[data-cart-drawer-bar]');
    if (openBar) openBar.classList.add('is-hidden');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll();
    // WCAG 2.4.3: remember where focus came from so closeDrawer can put it back.
    // Captured AFTER the is-open guard above, so a re-entrant reveal cannot
    // overwrite the real trigger with an element inside the drawer itself.
    if (!panel || !panel.contains(document.activeElement)) {
      lastFocusedBeforeOpen = document.activeElement;
    }
    if (panel) panel.focus();
  }

  function openDrawer() {
    // Guard re-entry: opening an already-open drawer must NOT increment the
    // scroll-lock refcount (a second lockScroll() with only one matching close
    // leaves body{position:fixed} stuck → page scroll-locked behind a closed
    // drawer). Header-button re-clicks and the public open() API both reach here.
    if (drawer.classList.contains('is-open')) {
      refreshDrawer().then(reconcile).catch(function () {});
      return;
    }
    // Intentional "view my cart" open (header icon / public API): reveal
    // instantly — the server-rendered DOM usually already matches the cart, so
    // snappiness matters more than the rare stale frame. reconcile() (which runs
    // after the refresh) re-establishes the scroll context for iOS via its kick.
    revealDrawer();
    // Always reflect the live cart when the drawer opens — covers carts
    // changed since page render (e.g. on another tab, or via the PDP ATC).
    refreshDrawer().then(reconcile).catch(function (err) {
      // eslint-disable-next-line no-console
      console.error('[cart-drawer] open refresh failed', err);
    });
  }

  // Auto-open triggered by a successful add-to-cart. Unlike the header "view
  // cart" open, here the cart DEFINITELY just changed, so the pre-refresh DOM is
  // stale (often the empty-cart state on a first add). Revealing first would
  // show non-scrollable stale content for ~1s, then swap the scroll container's
  // innerHTML out from under the user — which on iOS Safari breaks
  // -webkit-overflow-scrolling until a re-touch ("can't scroll right after the
  // cart opens, works after a moment"). So we refresh the content WHILE the
  // panel is still off-screen, then reveal a fully-formed, scrollable drawer.
  function openDrawerAfterAdd() {
    if (drawer.classList.contains('is-open')) {
      refreshDrawer().then(reconcile).catch(function () {});
      return;
    }
    refreshDrawer().then(function () {
      revealDrawer();          // content is settled → reveal slides in correct DOM
      return reconcile();      // measure + arm sticky bar while visible
    }).catch(function (err) {
      // Network failure: fall back to revealing the (stale) drawer so the add
      // is never swallowed — better a stale cart than no cart.
      // eslint-disable-next-line no-console
      console.error('[cart-drawer] add-open refresh failed', err);
      revealDrawer();
      reconcile().catch(function () {});
    });
  }

  // iOS Safari ties the momentum-scroll layer of a -webkit-overflow-scrolling
  // container to its content; replacing that content via innerHTML (as
  // refreshDrawer does) can leave the fresh container un-scrollable to touch
  // until a reflow or re-touch. Briefly toggling the property forces WebKit to
  // re-establish the scroll context. No-op on engines that ignore the property;
  // does not move scrollTop. Called after each content swap (via reconcile()).
  function kickScrollContext() {
    var content = drawer.querySelector('[data-cart-drawer-content]')
      || drawer.querySelector('.cart-drawer__content');
    if (!content) return;
    var prev = content.style.webkitOverflowScrolling;
    content.style.webkitOverflowScrolling = 'auto';
    void content.offsetHeight; // force reflow between the two writes
    content.style.webkitOverflowScrolling = prev || 'touch';
  }

  function closeDrawer() {
    // Guard like revealDrawer: closing an already-closed drawer would run
    // unlockScroll at lockCount 0, which still calls scrollTo with a stale
    // scrollLockY — visible page jump.
    if (!drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();
    // WCAG 2.4.3: return focus to whatever opened the drawer. Without this,
    // Escape dumped the user onto an arbitrary nav link and keyboard users lost
    // their place in the page. Guard document.contains — refreshDrawer swaps
    // innerHTML, and the opener may have been re-rendered away.
    var restore = lastFocusedBeforeOpen;
    lastFocusedBeforeOpen = null;
    if (restore && typeof restore.focus === 'function' && document.contains(restore)) {
      restore.focus();
    }
  }

  var overlay = drawer.querySelector('[data-cart-drawer-overlay]');
  if (overlay) overlay.addEventListener('click', closeDrawer);

  var closeBtn = drawer.querySelector('[data-cart-drawer-close]');
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.keyCode === 27) && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  // --- Focus containment (WCAG 2.1.2) -------------------------------------
  // The panel is role="dialog" aria-modal="true", but nothing was stopping Tab
  // from walking out into the page behind it: of 25 Tabs with the drawer open,
  // 13 landed on content the user could not see. aria-modal alone does not
  // constrain the tab ring for sighted keyboard users, so enforce it here.
  //
  // The focusable set is recomputed on EVERY Tab rather than cached at open:
  // refreshDrawer() replaces the panel's innerHTML after each cart mutation, so
  // any cached list would point at detached nodes.
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),'
    + 'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function panelFocusables() {
    if (!panel) return [];
    return [].slice.call(panel.querySelectorAll(FOCUSABLE)).filter(function (el) {
      // offsetParent is null for display:none; the sticky bar hides one of the
      // two checkout buttons that way, and a hidden control must not be a stop.
      return el.offsetParent !== null && !el.hasAttribute('aria-hidden');
    });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' && e.keyCode !== 9) return;
    if (!drawer.classList.contains('is-open') || !panel) return;
    var items = panelFocusables();
    if (!items.length) { e.preventDefault(); panel.focus(); return; }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (!panel.contains(active)) {
      // Focus escaped (or sits on the panel wrapper) — pull it back to an edge.
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }
    if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  });

  // Intentional cart view: header cart icon / any [data-cart-drawer-open]
  // control. NOT the auto-open-after-add path (that calls openDrawer directly,
  // never routes through here) — so topture:view_cart reflects real "show me my
  // cart" intent, not the drawer popping after an add.
  function viewCartIntent() {
    openDrawer();
    fetchJSON('/cart.js').then(function (cart) {
      dispatchTopture('topture:view_cart', { item_count: cart.item_count, total_price: cart.total_price });
    }).catch(function () { dispatchTopture('topture:view_cart', {}); });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-cart-drawer-open]');
    if (!trigger) return;
    e.preventDefault();
    viewCartIntent();
  });

  if (/[?&]opencart=1\b/.test(location.search)) {
    // Deliberate "open my cart" deep-link → counts as an intentional view.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', viewCartIntent);
    } else {
      viewCartIntent();
    }
  }

  // ===========================================================================
  // LIVE BUNDLE EDITING — mirrors assets/bundle-cart.js, but scoped to the
  // drawer via document-level delegation so it survives innerHTML swaps from
  // the Section Rendering API. Owns: single-choice/required enforcement,
  // .is-checked card sync, order-summary recompute, and the cart writes
  // (add/change child lines + sync the parent's Includes props).
  // ===========================================================================

  // ---- Shared helpers (ported from bundle-cart.js) --------------------------

  // Strips ".00" itself — see the formatMoney note in bundle-cart.js.
  function formatMoney(cents) {
    var fmt = (window.toptureParentPricing && window.toptureParentPricing.moneyFormat)
      || (window.Shopify && Shopify.money_format)
      || '${{amount}}';
    var out = (window.Shopify && typeof Shopify.formatMoney === 'function')
      ? Shopify.formatMoney(cents, fmt)
      : '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return out.replace(/\.00(?=\D|$)/g, '');
  }

  function fetchJSON(url, opts) {
    return fetch(url, Object.assign({ credentials: 'same-origin' }, opts || {}))
      .then(function (r) {
        if (!r.ok) {
          return r.json().catch(function () { return null; }).then(function (body) {
            var err = new Error('HTTP ' + r.status);
            err.body = body;
            throw err;
          });
        }
        return r.json();
      });
  }

  // ===========================================================================
  // FIRST-PARTY ANALYTICS EVENTS — semantic cart events for our remote tracking
  // snippet. Dispatched on `window` (same channel as cartcount:update); purely
  // additive — cartcount:update keeps firing unchanged. Contract is owned by the
  // tracking app:
  //   topture:add_to_cart      — a genuine add (PDP / quick-add / bundle add)
  //   topture:view_cart        — user INTENTIONALLY opened the cart drawer
  //   topture:remove_from_cart — a full line (or whole bundle) removal
  // Rules: all ids bare-numeric; a single action can touch multiple lines (a
  // configured bundle = parent + selected packages), so add/remove carry an
  // items[] array. Bundle lines share one bundle_id; bundle_role is
  // 'parent' | 'component' | null (theme stores 'parent'/'child' internally).
  // In-drawer add-on edits are NOT adds/removes (they set `mutating`) — excluded.
  // ===========================================================================

  function toNumericId(v) {
    if (v == null) return null;
    var m = String(v).match(/(\d+)\s*$/); // trailing digits → strips gid://…/123
    return m ? parseInt(m[1], 10) : null;
  }

  function bundlePropVal(props, key) {
    if (!props) return null;
    if (Array.isArray(props)) {
      for (var i = 0; i < props.length; i++) if (props[i][0] === key) return props[i][1];
      return null;
    }
    return props[key] != null ? props[key] : null;
  }

  function mapBundleRole(role) {
    if (role === 'parent') return 'parent';
    if (role === 'child') return 'component';
    return null;
  }

  function dispatchTopture(name, detail) {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (e) { /* analytics must never break a cart action */ }
  }

  // The /cart/add RESPONSE reports the new cart-line TOTAL, not the amount just
  // added. Recover the per-variant DELTA from the REQUEST body so quantity is
  // the amount added in THIS action. Best-effort across JSON / urlencoded /
  // FormData; on failure we fall back to the response quantity.
  function parseAddRequestQty(body) {
    var map = {};
    function put(id, qty) {
      var n = toNumericId(id);
      if (n != null) map[n] = parseInt(qty, 10) || 1;
    }
    try {
      if (typeof body === 'string' && body) {
        var t = body.trim();
        if (t.charAt(0) === '{') {
          var j = JSON.parse(t);
          if (j && Array.isArray(j.items)) j.items.forEach(function (it) { put(it.id, it.quantity); });
          else if (j && j.id != null) put(j.id, j.quantity);
        } else {
          var sp = new URLSearchParams(t);
          if (sp.get('id')) put(sp.get('id'), sp.get('quantity') || 1);
        }
      } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
        if (body.get('id')) put(body.get('id'), body.get('quantity') || 1);
      } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        if (body.get('id')) put(body.get('id'), body.get('quantity') || 1);
      }
    } catch (e) { /* unparseable → response quantity is used instead */ }
    return map;
  }

  // Build + dispatch topture:add_to_cart from a successful POST /cart/add. The
  // response is either a single line object (single add) or { items:[…] } (bulk
  // bundle add) — normalize to a line array, enrich, and emit ONE event.
  function handleAddToCartAnalytics(reqBody, responseText) {
    if (mutating) return; // drawer-initiated add-on edit — not a user "add to cart"
    var lines;
    try {
      var data = JSON.parse(responseText);
      if (data && Array.isArray(data.items)) lines = data.items;
      else if (data && (data.variant_id != null || data.id != null)) lines = [data];
      else return;
    } catch (e) { return; }
    if (!lines || !lines.length) return;

    var qtyMap = parseAddRequestQty(reqBody);
    var bundleId = null;
    var items = [];
    lines.forEach(function (line) {
      var vid = toNumericId(line.variant_id != null ? line.variant_id : line.id);
      if (vid == null) return;
      var bid = bundlePropVal(line.properties, '_bundle_id');
      if (bid && !bundleId) bundleId = bid;
      var item = {
        variant_id: vid,
        product_id: toNumericId(line.product_id),
        quantity: (qtyMap[vid] != null) ? qtyMap[vid] : (line.quantity || 1),
        bundle_role: mapBundleRole(bundlePropVal(line.properties, '_bundle_role'))
      };
      if (line.product_title || line.title) item.product_title = line.product_title || line.title;
      if (line.variant_title) item.variant_title = line.variant_title;
      if (typeof line.price === 'number') item.price = line.price;
      items.push(item);
    });
    if (items.length) dispatchTopture('topture:add_to_cart', { bundle_id: bundleId, items: items });
  }

  function getLineBundleMeta(line) {
    if (!line || !line.properties) return { bundleId: null, role: null, parentVariant: null };
    var props = line.properties;
    var bundleId = null;
    var role = null;
    var parentVariant = null;
    if (Array.isArray(props)) {
      for (var i = 0; i < props.length; i++) {
        if (props[i][0] === '_bundle_id') bundleId = props[i][1];
        if (props[i][0] === '_bundle_role') role = props[i][1];
        if (props[i][0] === '_bundle_parent_variant') parentVariant = props[i][1];
      }
    } else {
      bundleId = props._bundle_id || null;
      role = props._bundle_role || null;
      parentVariant = props._bundle_parent_variant || null;
    }
    return { bundleId: bundleId, role: role, parentVariant: parentVariant };
  }

  function readStackPricing(stack) {
    var node = stack.querySelector('[data-cart-drawer-pricing]');
    if (!node) return { variants: {}, moneyFormat: null };
    try {
      return JSON.parse(node.textContent) || { variants: {}, moneyFormat: null };
    } catch (e) {
      return { variants: {}, moneyFormat: null };
    }
  }

  function generateBundleId() {
    if (window.crypto && typeof crypto.randomUUID === 'function') {
      return 'b_' + crypto.randomUUID();
    }
    return 'b_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
  }

  // ---- Accordion open-state (preserved across innerHTML refresh) ------------
  // The whole drawer content is innerHTML-swapped on every cart edit, wiping
  // any JS-set UI state. We track which stacks / summary breakdowns are open
  // (keyed by bundleId|lineKey) and re-apply after each swap — the same pattern
  // reconcile() uses to re-check the bundle boxes.
  var openStacks = new Set();      // body accordion (per main)
  var openBreakdowns = new Set();  // summary price breakdown (per main)
  var knownStackIds = new Set();   // stacks we've already adopted a default for

  function stackId(stack) {
    return stack.dataset.bundleId || stack.dataset.lineKey || '';
  }

  function applyOpenStacks() {
    drawer.querySelectorAll('[data-stack-kind="bundle"]').forEach(function (st) {
      var open = openStacks.has(stackId(st));
      st.classList.toggle('is-open', open);
      var toggle = st.querySelector('[data-stack-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  // Adopt the Liquid-rendered default for any stack we haven't seen yet (count
  // rule: a sole main is rendered is-open), drop ids no longer present, then
  // re-apply the tracked state so user toggles survive a refresh.
  function syncOpenStacksWithDom() {
    var present = new Set();
    var liquidOpen = new Set();
    drawer.querySelectorAll('[data-stack-kind="bundle"]').forEach(function (st) {
      var id = stackId(st);
      present.add(id);
      if (st.classList.contains('is-open')) liquidOpen.add(id);
    });
    // Did the SET of bundle mains change since the last sync (a main added or
    // removed)? Compare membership, not just size.
    var changed = present.size !== knownStackIds.size;
    if (!changed) {
      present.forEach(function (id) { if (!knownStackIds.has(id)) changed = true; });
    }
    if (changed) {
      // Structural change → reset to the Liquid count-based default: a sole main
      // renders expanded, 2+ render collapsed. So adding a 2nd product collapses
      // the first; removing back to one re-expands the survivor.
      openStacks = liquidOpen;
    } else {
      // Same mains (e.g. an add-on toggle refresh) → preserve the user's
      // expand/collapse choices; just drop any ids no longer present.
      openStacks.forEach(function (id) { if (!present.has(id)) openStacks.delete(id); });
    }
    knownStackIds = present;
    applyOpenStacks();
  }

  // ---- Global order-summary recompute ---------------------------------------
  // Aggregates a true cart-wide total across every stack: each bundle main's
  // (parent + checked add-ons), each simple line, plus aggregated shipping.
  // Writes per-main subtotals into the headers and builds the collapsible
  // per-main breakdown + simple rows in the single footer summary.

  var CHEVRON_SVG = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>';

  function priceRow(label, cents, extraClass) {
    var row = document.createElement('div');
    row.className = 'cart-preview__row' + (extraClass ? ' ' + extraClass : '');
    var l = document.createElement('span');
    l.className = 'cart-preview__label';
    l.textContent = label;
    var p = document.createElement('span');
    p.className = 'cart-preview__price money';
    p.textContent = formatMoney(cents);
    row.appendChild(l);
    row.appendChild(p);
    return row;
  }

  // ---- Applied discounts (URL/email codes + automatic discounts) ------------
  // /cart.js has no `discount_applications` — reconstruct from the cart-level
  // applications plus per-line allocations, and trust `total_discount` for the
  // amount to subtract (covers everything once, since all displayed line prices
  // are pre-discount originals).
  var cartDiscounts = null; // null until the first fresh /cart.js lands

  function readCartDiscounts(cart) {
    var byTitle = {};
    var order = [];
    function add(title, cents) {
      if (!cents || cents <= 0) return;
      var t = title || 'Discount';
      if (!Object.prototype.hasOwnProperty.call(byTitle, t)) { byTitle[t] = 0; order.push(t); }
      byTitle[t] += cents;
    }
    (cart.cart_level_discount_applications || []).forEach(function (a) {
      add(a.title || a.key, a.total_allocated_amount || 0);
    });
    (cart.items || []).forEach(function (it) {
      (it.line_level_discount_allocations || []).forEach(function (al) {
        add(al.discount_application && al.discount_application.title, al.amount || 0);
      });
    });
    var apps = order.map(function (t) { return { title: t, cents: byTitle[t] }; });
    var total = cart.total_discount || 0;
    // Amount mismatch safety net: always subtract total_discount; if the
    // itemized rows don't cover it, show one generic row for the remainder.
    var itemized = apps.reduce(function (s, a) { return s + a.cents; }, 0);
    if (total > itemized) apps.push({ title: 'Discount', cents: total - itemized });
    return { cents: total, apps: total > 0 ? apps : [] };
  }

  function noteCartDiscounts(cart) {
    var prev = cartDiscounts ? cartDiscounts.cents : null;
    cartDiscounts = readCartDiscounts(cart);
    return prev !== null && prev !== cartDiscounts.cents;
  }

  function currentDiscountCents() {
    if (cartDiscounts) return cartDiscounts.cents;
    // Before any fresh cart fetch, trust the server-stamped value.
    var c = drawer.querySelector('[data-summary-discounts]');
    return c ? (parseInt(c.getAttribute('data-cart-discount-cents'), 10) || 0) : 0;
  }

  function renderDiscounts() {
    var container = drawer.querySelector('[data-summary-discounts]');
    if (!container) return;
    // Without live data, leave the server-rendered rows untouched.
    if (!cartDiscounts) return;
    container.replaceChildren();
    container.setAttribute('data-cart-discount-cents', String(cartDiscounts.cents));
    cartDiscounts.apps.forEach(function (a) {
      // Codes are often opaque (e.g. "2MFJWZNG") — prefix so the row explains
      // itself; the generic fallback title is already just "Discount".
      var label = a.title === 'Discount' ? 'Discount' : 'Discount (' + a.title + ')';
      var row = priceRow(label, a.cents, 'cart-preview__row--discount');
      row.querySelector('.cart-preview__label').className += ' cart-preview__label--discount';
      var price = row.querySelector('.cart-preview__price');
      price.className += ' cart-preview__price--discount';
      price.textContent = '−' + formatMoney(a.cents);
      container.appendChild(row);
    });
  }

  function buildMainRow(m) {
    var wrap = document.createElement('div');
    wrap.className = 'cart-preview__main';
    wrap.dataset.breakdownId = m.id;
    var isOpen = openBreakdowns.has(m.id);
    if (isOpen) wrap.classList.add('is-open');

    var head = document.createElement('button');
    head.type = 'button';
    head.className = 'cart-preview__main-head';
    head.setAttribute('data-breakdown-toggle', '');
    head.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    var title = document.createElement('span');
    title.className = 'cart-preview__label';
    title.textContent = m.quantity > 1 ? m.title + ' × ' + m.quantity : m.title;
    var right = document.createElement('span');
    right.className = 'cart-preview__main-head-right';
    var price = document.createElement('span');
    price.className = 'cart-preview__price money';
    price.textContent = formatMoney(m.subtotal);
    var chev = document.createElement('span');
    chev.className = 'cart-preview__chevron';
    chev.setAttribute('aria-hidden', 'true');
    chev.innerHTML = CHEVRON_SVG;
    right.appendChild(price);
    right.appendChild(chev);
    head.appendChild(title);
    head.appendChild(right);

    var body = document.createElement('div');
    body.className = 'cart-preview__main-body';
    var inner = document.createElement('div');
    inner.className = 'cart-preview__main-body-inner';
    inner.appendChild(priceRow(
      m.quantity > 1 ? m.title + ' × ' + m.quantity : m.title,
      m.parentCents,
      'cart-preview__row--addon'
    ));
    m.addOns.forEach(function (a) {
      inner.appendChild(priceRow(a.title, a.price, 'cart-preview__row--addon'));
    });
    body.appendChild(inner);

    wrap.appendChild(head);
    wrap.appendChild(body);
    return wrap;
  }

  // Aggregate shipping across bundle mains: FREE only if every main is free;
  // else sum the known fixed costs; if any main is neither free nor priced,
  // show "Calculated at checkout" and fold only the known sum into the total.
  function computeShipping() {
    var stacks = drawer.querySelectorAll('[data-stack-kind="bundle"]');
    if (!stacks.length) return { cents: 0, free: false, unknown: true };
    var allFree = true;
    var knownSum = 0;
    var anyUnknown = false;
    stacks.forEach(function (st) {
      var free = st.dataset.freeShipping === 'true';
      var cents = parseInt(st.dataset.shippingCents, 10) || 0;
      if (free) return;          // free → contributes 0
      allFree = false;
      if (cents > 0) knownSum += cents;
      else anyUnknown = true;    // not free, no price → unknown
    });
    if (allFree) return { cents: 0, free: true, unknown: false };
    return { cents: knownSum, free: false, unknown: anyUnknown };
  }

  function renderShipping(s) {
    var row = drawer.querySelector('[data-summary-shipping]');
    if (!row) return;
    var label = row.querySelector('.cart-preview__label');
    var val = row.querySelector('[data-summary-shipping-value]');
    if (label) {
      label.textContent = s.free ? 'Free shipping (contiguous US)' : 'Shipping (contiguous US)';
      label.classList.toggle('cart-preview__label--free-shipping', s.free);
    }
    if (!val) return;
    if (s.free) {
      val.className = 'cart-preview__price cart-preview__price--free-shipping';
      val.textContent = 'FREE';
    } else if (s.unknown) {
      val.className = 'cart-preview__price cart-preview__shipping-tbd';
      val.textContent = 'Calculated at checkout';
    } else {
      val.className = 'cart-preview__price money';
      val.textContent = formatMoney(s.cents);
    }
  }

  function recomputeSummary() {
    var grand = 0;

    // Bundle mains: parent + checked add-ons → per-main subtotal.
    var mains = [];
    drawer.querySelectorAll('[data-stack-kind="bundle"]').forEach(function (stack) {
      var pricing = readStackPricing(stack);
      var variants = pricing.variants || {};
      var parentEntry = variants[stack.dataset.parentVariant];
      if (!parentEntry) {
        var firstKey = Object.keys(variants)[0];
        parentEntry = firstKey ? variants[firstKey] : null;
      }
      var parentCents = parentEntry ? parentEntry.price : 0;
      // Property-less adds of the same product merge into this line, so the
      // parent can carry qty ≥ 2 — price is per-unit and must be multiplied.
      var lineQty = parseInt(stack.dataset.lineQuantity, 10) || 1;
      var parentLineCents = parentCents * lineQty;

      var sumAddons = 0;
      var addOns = [];
      stack.querySelectorAll('[data-bundle-input]:checked').forEach(function (el) {
        var price = parseInt(el.getAttribute('data-variant-price'), 10) || 0;
        sumAddons += price;
        addOns.push({ title: el.getAttribute('data-variant-title') || '', price: price });
      });

      var subtotal = parentLineCents + sumAddons;
      grand += subtotal;

      var slot = stack.querySelector('[data-stack-subtotal]');
      if (slot) slot.textContent = formatMoney(subtotal);

      mains.push({
        id: stackId(stack),
        title: stack.dataset.productTitle || '',
        quantity: lineQty,
        parentCents: parentLineCents,
        addOns: addOns,
        subtotal: subtotal
      });
    });

    // Simple (non-bundle) lines. linePrice is the LINE total (unit × qty), so
    // carry the quantity for the row label to make the math legible.
    var simples = [];
    drawer.querySelectorAll('[data-stack-kind="simple"]').forEach(function (s) {
      var cents = parseInt(s.dataset.linePrice, 10) || 0;
      var qty = parseInt(s.dataset.lineQuantity, 10) || 1;
      grand += cents;
      simples.push({ title: s.dataset.productTitle || '', price: cents, quantity: qty });
    });

    var shipping = computeShipping();
    grand += shipping.cents;

    // Subtract applied discounts once (all row prices above are pre-discount).
    var discountCents = currentDiscountCents();
    if (discountCents > 0) grand = Math.max(0, grand - discountCents);
    renderDiscounts();

    var mainsC = drawer.querySelector('[data-summary-mains]');
    if (mainsC) {
      mainsC.replaceChildren();
      mains.forEach(function (m) { mainsC.appendChild(buildMainRow(m)); });
    }
    var simpleC = drawer.querySelector('[data-summary-simple]');
    if (simpleC) {
      simpleC.replaceChildren();
      simples.forEach(function (s) {
        var label = s.quantity > 1 ? s.title + ' × ' + s.quantity : s.title;
        simpleC.appendChild(priceRow(label, s.price, 'cart-preview__row--simple'));
      });
    }
    renderShipping(shipping);
    // Two totals share [data-cart-preview-total]: the in-body summary and the
    // floating bar shortcut. Update both.
    var totalStr = formatMoney(grand);
    drawer.querySelectorAll('[data-cart-preview-total]').forEach(function (el) {
      el.textContent = totalStr;
    });
  }

  // ---- Floating-bar visibility ----------------------------------------------
  // The slim bar (Total + Checkout) is a shortcut shown ONLY while the real
  // order-summary CTA is below the fold. When the bottom sentinel scrolls into
  // the content viewport, the real summary is visible → hide the bar so the
  // summary reads as a normal cart. IntersectionObserver re-fires on any
  // geometry change (scroll, accordion expand, add-on toggle), so we don't need
  // a scroll listener. Re-armed after each section refresh (DOM is rebuilt).
  var stickyObserver = null;

  function setupStickyBar() {
    if (stickyObserver) { stickyObserver.disconnect(); stickyObserver = null; }
    var content = drawer.querySelector('[data-cart-drawer-content]');
    var bar = drawer.querySelector('[data-cart-drawer-bar]');
    if (!content || !bar) return;
    // Observe the REAL (in-summary) checkout button, NOT a bottom sentinel:
    // the bar must vanish as the real CTA approaches so the two Proceed-to-
    // Checkout buttons never overlap. Fall back to the bottom sentinel.
    var target = content.querySelector('.cart-drawer__summary button[name="checkout"]')
      || content.querySelector('[data-summary-end]');
    if (!target || !('IntersectionObserver' in window)) {
      bar.classList.remove('is-hidden'); // fallback: keep the CTA reachable
      return;
    }
    // Measure the bar while visible, then trigger ~one bar-height EARLY (via
    // rootMargin) so the bar is gone before the real CTA reaches the zone the
    // bar occupies — no moment where both buttons show.
    bar.classList.remove('is-hidden');
    var lead = (bar.offsetHeight || 150) + 16;

    // Synchronous initial state (avoid a flash before the first async callback).
    var cr = content.getBoundingClientRect();
    var tr = target.getBoundingClientRect();
    var notScrollable = content.scrollHeight <= content.clientHeight + 2;
    var targetNear = tr.top < (cr.bottom + lead) && tr.bottom > cr.top;
    bar.classList.toggle('is-hidden', notScrollable || targetNear);

    stickyObserver = new IntersectionObserver(function (entries) {
      var last = entries[entries.length - 1];
      bar.classList.toggle('is-hidden', last.isIntersecting);
    }, { root: content, rootMargin: '0px 0px ' + lead + 'px 0px', threshold: 0 });
    stickyObserver.observe(target);
  }

  // ---- Accordion toggle handlers --------------------------------------------

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest && e.target.closest('[data-stack-toggle]');
    if (!toggle || !drawer.contains(toggle)) return;
    if (e.target.closest('[data-cart-drawer-remove]')) return; // remove owns its click
    var stack = toggle.closest('[data-cart-drawer-stack]');
    if (!stack) return;
    var id = stackId(stack);
    var open = !stack.classList.contains('is-open');
    stack.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) openStacks.add(id); else openStacks.delete(id);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.keyCode !== 13 && e.keyCode !== 32) return;
    var toggle = e.target.closest && e.target.closest('[data-stack-toggle]');
    if (!toggle || !drawer.contains(toggle)) return;
    if (e.target.closest('[data-cart-drawer-remove]')) return;
    e.preventDefault();
    toggle.click();
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-breakdown-toggle]');
    if (!btn || !drawer.contains(btn)) return;
    var wrap = btn.closest('[data-breakdown-id]');
    if (!wrap) return;
    var id = wrap.dataset.breakdownId;
    var open = !wrap.classList.contains('is-open');
    wrap.classList.toggle('is-open', open);
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) openBreakdowns.add(id); else openBreakdowns.delete(id);
  });

  // ---- .is-checked card sync (port of syncCardClass) ------------------------

  function syncIsChecked(scopeEl) {
    if (!scopeEl) return;
    scopeEl.querySelectorAll('[data-bundle-input]').forEach(function (input) {
      var card = input.closest('[data-bundle-option]');
      if (!card) return;
      if (input.checked) card.classList.add('is-checked');
      else card.classList.remove('is-checked');
    });
  }

  // ---- Sold-out options: drawer-side guard ----------------------------------
  // Liquid marks sold-out options in every render (data-variant-available,
  // aria-disabled="true", .bundle-option--soldout + the Sold Out badge —
  // shared markup/CSS in bundle-selector.liquid / bundle-selector.css); the
  // actual `disabled` is applied contextually here: unchecked sold-out inputs
  // are disabled so a dead-end pick can't happen in the UI, while a CHECKED
  // sold-out input stays enabled — unchecking it is a removal, which must
  // remain possible (and is required before the stack can commit again) — and
  // its aria-disabled is cleared so AT doesn't announce an operable control as
  // disabled. The commitStack preflight stays as the safety net. Re-applied
  // after every innerHTML swap (via reconcile), same as the checkbox state.
  function applySoldOutState() {
    drawer.querySelectorAll('[data-bundle-input]').forEach(function (input) {
      var soldOut = input.getAttribute('data-variant-available') !== 'true';
      var disable = soldOut && !input.checked;
      input.disabled = disable;
      if (disable) input.setAttribute('aria-disabled', 'true');
      else input.removeAttribute('aria-disabled');
      if (!soldOut) return;
      // Belt-and-suspenders: the shared markup renders the badge — only append
      // one if it's somehow absent, reusing the shared classes.
      var card = input.closest('[data-bundle-option]');
      if (!card || card.querySelector('.bundle-option__badge--soldout')) return;
      var main = card.querySelector('.bundle-option__main');
      if (!main) return;
      var badge = document.createElement('span');
      badge.className = 'bundle-option__badge bundle-option__badge--soldout';
      badge.textContent = 'Sold Out';
      main.insertBefore(badge, main.firstChild);
    });
  }

  // ---- Section Rendering API: swap the drawer content -----------------------

  // ---- Scroll position (preserved across innerHTML refresh) -----------------
  // The innerHTML swap recreates .cart-drawer__content, resetting its scrollTop
  // to 0 — on mobile an add-on commit deep in the drawer snapped the view back
  // to the top ~8s later (e2e-233 HIGH warning). Same pattern as the accordion
  // (openStacks) / checkbox (reconcile) state: capture right before the swap,
  // re-apply right after (so the first paint isn't at the top), and once more
  // after reconcile() re-expands the accordions and settles the final
  // scrollHeight — clamped both times, since the refreshed content can be
  // shorter than where the user was.
  var pendingScrollTop = null;

  function scrollContent() {
    return drawer.querySelector('[data-cart-drawer-content]')
      || drawer.querySelector('.cart-drawer__content');
  }

  function restorePendingScrollTop(clear) {
    if (pendingScrollTop === null) return;
    var content = scrollContent();
    if (content) {
      content.scrollTop = Math.min(
        pendingScrollTop,
        Math.max(0, content.scrollHeight - content.clientHeight)
      );
    }
    if (clear) pendingScrollTop = null;
  }

  function refreshDrawer() {
    return fetchJSON('/?sections=cart-drawer').then(function (data) {
      var html = data && data['cart-drawer'];
      if (typeof html !== 'string') {
        throw new Error('[cart-drawer] section render missing "cart-drawer" key');
      }
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      // Swap the BODY wrapper (scroll content + pinned bar) so the bar appears/
      // disappears correctly across empty↔non-empty transitions. Preserve the
      // outer [data-cart-drawer] open/aria state.
      var freshBody = tmp.querySelector('[data-cart-drawer-body]');
      var liveBody = drawer.querySelector('[data-cart-drawer-body]');
      if (freshBody && liveBody) {
        // Only preserve scroll for refreshes of an OPEN drawer. The closed
        // drawer is visibility:hidden (not display:none), so its content keeps
        // the previous session's scrollTop — restoring that on an ATC auto-open
        // would reveal the drawer scrolled deep into the new cart.
        var preSwap = drawer.classList.contains('is-open') ? scrollContent() : null;
        pendingScrollTop = preSwap && preSwap.scrollTop > 0 ? preSwap.scrollTop : null;
        liveBody.innerHTML = freshBody.innerHTML;
        restorePendingScrollTop(false); // keep for the post-reconcile re-clamp
        // The freshly-rendered bar defaults to .is-hidden (markup), so it stays
        // hidden through the open/refresh transient. The single authoritative
        // visibility decision happens in reconcile() AFTER the summary rows are
        // filled and the accordion state is applied — measuring the settled
        // geometry, so the bar can't reveal-then-hide mid-open.
      }
      return data;
    });
  }

  // ---- Reconcile DOM <- live cart -------------------------------------------

  function buildBundleChildSets(cart) {
    // bundleId -> { value -> lineKey } for child lines.
    var map = {};
    (cart.items || []).forEach(function (line) {
      var meta = getLineBundleMeta(line);
      if (meta.role !== 'child' || !meta.bundleId) return;
      if (!map[meta.bundleId]) map[meta.bundleId] = {};
      map[meta.bundleId][String(line.variant_id)] = line.key;
    });
    return map;
  }

  function reconcile() {
    return fetchJSON('/cart.js').then(function (cart) {
      noteCartDiscounts(cart); // before recomputeSummary reads the cache
      var childMap = buildBundleChildSets(cart);
      drawer.querySelectorAll('[data-cart-drawer-stack]').forEach(function (stack) {
        var bundleId = stack.dataset.bundleId;
        var childSet = (bundleId && childMap[bundleId]) || {};
        stack.querySelectorAll('[data-bundle-input]').forEach(function (input) {
          // Programmatic .checked does NOT fire 'change', so reconcile is safe.
          input.checked = Object.prototype.hasOwnProperty.call(childSet, String(input.value));
        });
        syncIsChecked(stack);
      });
      applySoldOutState();
      syncOpenStacksWithDom();
      recomputeSummary();
      setupStickyBar();
      kickScrollContext(); // re-arm iOS touch-scroll after the innerHTML swap
      restorePendingScrollTop(true); // final clamp at the settled scrollHeight
      updateCount(cart);
      return cart;
    });
  }

  // Header count = every line item (main product + each selected option).
  // Light enough to call after an in-place commit without re-rendering the DOM.
  function updateCount(cart) {
    var countEl = drawer.querySelector('[data-cart-drawer-count]');
    if (countEl) {
      var n = cart.item_count || 0;
      countEl.textContent = '(' + n + ' ' + (n === 1 ? 'item' : 'items') + ')';
    }
    // Cart mutations can change the discount amount (e.g. % codes, thresholds).
    // Refresh the cache from this fresh cart; if the amount changed, re-run the
    // summary. Change-guarded, so the reconcile() call path can't recurse.
    if (noteCartDiscounts(cart)) recomputeSummary();
    window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
    return cart;
  }

  window.ToptureCartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    refresh: function () { return refreshDrawer().then(reconcile); }
  };

  // ===========================================================================
  // AUTO-OPEN ON ADD-TO-CART — transparent fetch + XHR interceptors that detect
  // a successful POST to /cart/add(.js) and pop the drawer open. Covers the
  // plain-product PDP add (Empire uses jQuery $.ajax → XHR) and any other
  // add path that doesn't already drive the drawer itself.
  //
  // The bundle PDP path (bundle-cart.js) calls ToptureCartDrawer.open()
  // directly on success; this interceptor would also fire for that POST, but
  // openDrawer() is idempotent and we guard against a redundant second open
  // (and its refresh) when the drawer is already open + debounce, so there's
  // no double-fetch / flash.
  //
  // Empire's plain-product redirect (settings.cart_redirection) is gated OFF
  // in static-product.liquid when enable_slide_cart is on, so the page does
  // NOT unload after the add — leaving this interceptor free to open the
  // drawer. When slide cart is OFF, every branch below early-returns and the
  // wrappers are pure pass-throughs.
  // ===========================================================================

  function isCartAddUrl(url) {
    if (!url) return false;
    var u = String(url);
    // Match /cart/add and /cart/add.js (with or without query/host).
    return /\/cart\/add(\.js)?(\?|$)/.test(u);
  }

  var lastAutoOpen = 0;

  function maybeAutoOpenAfterAdd() {
    if (!(window.Theme && window.Theme.slideCartEnabled)) return;
    if (mutating) return;                 // drawer-initiated cart write in flight
    if (drawer.classList.contains('is-open')) return; // already open — avoid re-fetch/flash
    var now = Date.now();
    if (now - lastAutoOpen < 600) return; // debounce duplicate add signals
    lastAutoOpen = now;
    // Refresh-first open: populate the drawer WHILE it's still off-screen, then
    // reveal a settled, scrollable panel — no stale non-scrollable frame and no
    // scroll-container swap mid-touch (the iOS "can't scroll right after add" bug).
    openDrawerAfterAdd();
  }

  // ---- fetch wrapper --------------------------------------------------------
  if (typeof window.fetch === 'function' && !window.fetch.__toptureCartWrapped) {
    var origFetch = window.fetch;
    var wrappedFetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url);
      var method = (init && init.method)
        || (input && input.method)
        || 'GET';
      var isAdd = isCartAddUrl(url) && String(method).toUpperCase() === 'POST';
      var p = origFetch.apply(this, arguments);
      if (!isAdd) return p;
      return p.then(function (res) {
        // Only act on a genuinely successful add.
        if (res && res.ok) {
          try {
            var reqBody = init && init.body; // bundle add passes a JSON string here
            res.clone().text()
              .then(function (txt) { handleAddToCartAnalytics(reqBody, txt); })
              .catch(function () {});
          } catch (e) { /* clone unsupported → skip analytics, never break fetch */ }
          try { maybeAutoOpenAfterAdd(); } catch (e) { /* never break the fetch */ }
        }
        return res;
      });
    };
    wrappedFetch.__toptureCartWrapped = true;
    window.fetch = wrappedFetch;
  }

  // ---- XMLHttpRequest wrapper (jQuery $.ajax, Empire plain-product add) ------
  if (window.XMLHttpRequest && window.XMLHttpRequest.prototype
      && !window.XMLHttpRequest.prototype.__toptureCartWrapped) {
    var XHRProto = window.XMLHttpRequest.prototype;
    var origOpen = XHRProto.open;
    var origSend = XHRProto.send;

    XHRProto.open = function (method, url) {
      this.__toptureMethod = method;
      this.__toptureUrl = url;
      return origOpen.apply(this, arguments);
    };

    XHRProto.send = function (body) {
      var self = this;
      var isAdd = isCartAddUrl(self.__toptureUrl)
        && String(self.__toptureMethod || 'GET').toUpperCase() === 'POST';
      if (isAdd) {
        self.addEventListener('load', function () {
          if (self.status >= 200 && self.status < 300) {
            try {
              var txt = '';
              try { txt = self.responseText; }
              catch (e1) { try { txt = JSON.stringify(self.response); } catch (e2) { txt = ''; } }
              handleAddToCartAnalytics(body, txt);
            } catch (e) { /* never break the XHR */ }
            try { maybeAutoOpenAfterAdd(); } catch (e) { /* never break the XHR */ }
          }
        });
      }
      return origSend.apply(this, arguments);
    };

    XHRProto.__toptureCartWrapped = true;
  }

  // ---- Mutation lock + per-stack debounce -----------------------------------

  var mutating = false;            // a commit's network sequence is in flight
  var debounceTimers = {};         // keyed by parent lineKey
  var pendingStacks = {};          // stackKey -> stack: clicks made during an in-flight commit
  var pendingRemoves = {};         // stackKey -> {node, lineKey, bundleId}: remove clicks made during an in-flight mutation

  function stackKey(stack) {
    return stack.dataset.lineKey || stack.dataset.bundleId || 'stack';
  }

  function findStackByKeys(lineKey, bundleId) {
    var stacks = drawer.querySelectorAll('[data-cart-drawer-stack]');
    for (var i = 0; i < stacks.length; i++) {
      var ds = stacks[i].dataset;
      if ((lineKey && ds.lineKey === lineKey) || (bundleId && ds.bundleId === bundleId)) return stacks[i];
    }
    return null;
  }

  // After a commit settles, run the next stack that the user touched mid-flight
  // so no click is ever lost (we coalesce instead of dropping).
  function flushPending() {
    // Queued removals run first — a remove supersedes any pending add-on
    // commit for the same stack (that commit was cleared when the remove was
    // queued; anything else left here targets a different stack).
    var rkeys = Object.keys(pendingRemoves);
    for (var j = 0; j < rkeys.length; j++) {
      var entry = pendingRemoves[rkeys[j]];
      delete pendingRemoves[rkeys[j]];
      if (!entry) continue;
      var rs = entry.node;
      // An innerHTML swap (refreshDrawer) may have detached the queued node —
      // re-resolve against the fresh DOM (line keys can change across commits,
      // so bundleId is the fallback) so the remove isn't silently dropped.
      // No match at all means the line already left the cart.
      if (rs && !rs.isConnected) rs = findStackByKeys(entry.lineKey, entry.bundleId);
      if (rs && rs.isConnected) { removeStack(rs); return; }
    }
    var keys = Object.keys(pendingStacks);
    for (var i = 0; i < keys.length; i++) {
      var st = pendingStacks[keys[i]];
      delete pendingStacks[keys[i]];
      // Skip stacks detached by a remove/refresh — committing a detached node
      // would write stale state (e.g. re-add a just-removed bundle).
      if (st && st.isConnected) { commitStack(st); return; }
    }
  }

  function scheduleCommit(stack) {
    var key = stackKey(stack);
    if (debounceTimers[key]) clearTimeout(debounceTimers[key]);
    debounceTimers[key] = setTimeout(function () {
      delete debounceTimers[key];
      commitStack(stack);
    }, 250);
  }

  // ---- Delegated change handler on drawer add-on checkboxes -----------------

  document.addEventListener('change', function (e) {
    var input = e.target.closest && e.target.closest('[data-bundle-input]');
    if (!input) return;
    if (!drawer.contains(input)) return;
    // NOTE: do NOT bail when `mutating` — a user click during an in-flight
    // commit must still update the UI instantly and be captured. commitStack()
    // coalesces (queues a re-commit) rather than dropping the change. Bailing
    // here was the cause of "click does nothing / deselect doesn't stick":
    // the native checkbox toggled but the change was ignored, then the trailing
    // refresh reverted it from the stale cart.

    var stack = input.closest('[data-cart-drawer-stack]');
    var section = input.closest('[data-bundle-section]');
    if (!stack || !section) return;

    var required = section.getAttribute('data-required') === 'true';
    var selType = section.getAttribute('data-selection-type');

    // single-choice: when one becomes checked, uncheck its siblings.
    if (selType === 'single' && input.checked) {
      section.querySelectorAll('[data-bundle-input]').forEach(function (other) {
        if (other !== input && other.checked) other.checked = false;
      });
    }

    // required: never allow a required section to end up empty — unless the
    // unchecked option is sold out. Bouncing a sold-out option back would wedge
    // the stack: it can never pass the commit preflight while checked, and
    // unchecking is the only way out. Prefer swapping to an available sibling;
    // with none available, let the section empty so the customer isn't trapped.
    if (required) {
      var anyChecked = section.querySelectorAll('[data-bundle-input]:checked').length > 0;
      if (!anyChecked) {
        if (input.getAttribute('data-variant-available') === 'true') {
          input.checked = true;
        } else {
          var replacement = Array.prototype.find.call(
            section.querySelectorAll('[data-bundle-input]'),
            function (other) {
              return other !== input && other.getAttribute('data-variant-available') === 'true';
            }
          );
          if (replacement) replacement.checked = true;
        }
      }
    }

    syncIsChecked(section);
    applySoldOutState(); // a just-unchecked sold-out option locks immediately
    recomputeSummary(); // snappy optimistic UI

    scheduleCommit(stack);
  });

  // ---- The cart write -------------------------------------------------------

  function setStackLoading(stack, on) {
    if (on) stack.classList.add('cart-drawer__stack--loading');
    else stack.classList.remove('cart-drawer__stack--loading');
  }

  function findParentLine(cart, bundleId, fallbackKey) {
    // After adds/changes, the parent line key can change. Re-resolve by
    // (bundleId + role parent) first, then fall back to the stale key.
    var byBundle = (cart.items || []).find(function (it) {
      var m = getLineBundleMeta(it);
      return m.role === 'parent' && m.bundleId === bundleId;
    });
    if (byBundle) return byBundle;
    return (cart.items || []).find(function (it) { return it.key === fallbackKey; }) || null;
  }

  function findParentLineKey(cart, bundleId, fallbackKey) {
    var line = findParentLine(cart, bundleId, fallbackKey);
    return line ? line.key : fallbackKey;
  }

  function buildParentProps(bundleId, desiredTitles) {
    // change.js REPLACES the properties map, so we re-emit the bundle props
    // plus a numbered Includes N line per desired child (cart-item.liquid
    // renders these as green-check rows).
    var props = { _bundle_id: bundleId, _bundle_role: 'parent' };
    desiredTitles.forEach(function (title, i) {
      props['Includes ' + (i + 1)] = title;
    });
    return props;
  }

  function cartChange(body) {
    return fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function cartAdd(body) {
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function cartUpdate(updates) {
    return fetchJSON('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });
  }

  // ---- Remove a whole stack: the parent line + all its bundle children -------
  function removeStack(stack) {
    var bundleId = stack.dataset.bundleId;
    var parentKey = stack.dataset.lineKey;
    mutating = true;
    setStackLoading(stack, true);
    var removedItems = [];                 // snapshot for topture:remove_from_cart
    var removedBundleId = bundleId || null;
    fetchJSON('/cart.js').then(function (cart) {
      var updates = {};
      if (parentKey) updates[parentKey] = 0;
      // Cascade: zero out every line sharing this bundle id (parent + children).
      if (bundleId) {
        (cart.items || []).forEach(function (line) {
          if (getLineBundleMeta(line).bundleId === bundleId) updates[line.key] = 0;
        });
      }
      // Snapshot the lines being removed (parent + cascaded children) for the
      // analytics event — built BEFORE the write while we still have the cart.
      (cart.items || []).forEach(function (line) {
        if (updates[line.key] !== 0) return;
        var vid = toNumericId(line.variant_id != null ? line.variant_id : line.id);
        if (vid == null) return;
        var meta = getLineBundleMeta(line);
        if (meta.bundleId && !removedBundleId) removedBundleId = meta.bundleId;
        var item = {
          variant_id: vid,
          product_id: toNumericId(line.product_id),
          quantity: line.quantity,        // full line qty (a full-line removal)
          bundle_role: mapBundleRole(meta.role)
        };
        if (line.product_title || line.title) item.product_title = line.product_title || line.title;
        removedItems.push(item);
      });
      return cartUpdate(updates); // atomic — one request
    }).then(function () {
      // Fire once the removal is confirmed.
      if (removedItems.length) {
        dispatchTopture('topture:remove_from_cart', { bundle_id: removedBundleId, items: removedItems });
      }
      return fetchJSON('/cart.js');
    }).then(function (cart) {
      if ((cart.item_count || 0) === 0) {
        // Cart emptied — need the section re-render to show the empty state.
        return refreshDrawer().then(reconcile);
      }
      // Otherwise just drop this one stack's DOM and resync the rest in place
      // (no full re-render → no scroll jump on the remaining items).
      stack.remove();
      return reconcile();
    }).then(function () {
      mutating = false;
      flushPending();
    }).catch(function (err) {
      // eslint-disable-next-line no-console
      console.error('[cart-drawer] remove failed', err);
      // eslint-disable-next-line no-alert
      window.alert('Could not remove this item. Please try again.');
      refreshDrawer().then(reconcile)
        .catch(function () { /* refresh also failed — leave DOM as-is */ })
        .finally(function () { mutating = false; flushPending(); });
    });
  }

  // Delegated remove-button handler (scoped to the drawer). A click while a
  // mutation is in flight is QUEUED, not dropped — the running mutation's
  // flushPending() runs it on settle, same coalescing pattern as the add-on
  // toggles, so the user's intent is never silently lost.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-cart-drawer-remove]');
    if (!btn || !drawer.contains(btn)) return;
    e.preventDefault();
    var stack = btn.closest('[data-cart-drawer-stack]');
    if (!stack) return;
    if (mutating) {
      var key = stackKey(stack);
      // The remove supersedes any queued/debounced add-on commit for this stack.
      delete pendingStacks[key];
      if (debounceTimers[key]) { clearTimeout(debounceTimers[key]); delete debounceTimers[key]; }
      pendingRemoves[key] = {
        node: stack,
        lineKey: stack.dataset.lineKey || '',
        bundleId: stack.dataset.bundleId || ''
      };
      setStackLoading(stack, true); // busy cue until the queued remove runs
      return;
    }
    removeStack(stack);
  });

  function commitStack(stack) {
    // Coalesce: if a commit is already in flight, remember this stack and bail.
    // The running commit re-runs us (flushPending) when it settles, reading the
    // LATEST DOM selection — so rapid clicks converge instead of being dropped.
    if (mutating) { pendingStacks[stackKey(stack)] = stack; return; }

    // Snapshot the desired add-on selection from the DOM.
    var checkedInputs = Array.prototype.slice.call(
      stack.querySelectorAll('[data-bundle-input]:checked')
    );

    // Preflight: bail on any sold-out selection.
    var soldOut = checkedInputs.filter(function (el) {
      return el.getAttribute('data-variant-available') !== 'true';
    });
    if (soldOut.length) {
      // eslint-disable-next-line no-alert
      window.alert('One of the selected items is currently sold out: '
        + soldOut.map(function (el) { return el.getAttribute('data-variant-title'); }).join(', '));
      // Revert UI to the live cart (in place — no full re-render).
      reconcile().catch(function () { /* failed /cart.js GET — checkbox stays; next commit resyncs */ });
      return;
    }

    var desiredValues = checkedInputs.map(function (el) { return String(el.value); });
    var desiredTitles = checkedInputs.map(function (el) {
      return el.getAttribute('data-variant-title') || '';
    });
    var parentVariant = stack.dataset.parentVariant;
    var hadBundleId = !!stack.dataset.bundleId;
    var bundleId = stack.dataset.bundleId || generateBundleId();
    var parentLineKey = stack.dataset.lineKey;

    mutating = true;
    setStackLoading(stack, true);

    var seq = Promise.resolve();

    // Phase 0 — if the parent had NO bundleId yet, stamp the bundle props on
    // it first so the new children can attach to a real bundle group.
    if (!hadBundleId) {
      seq = seq.then(function () {
        return fetchJSON('/cart.js').then(function (cart) {
          var line = findParentLine(cart, bundleId, parentLineKey);
          // Preserve the line's current quantity — a property-less add of the
          // same product merges into this line (qty ≥ 2), and change.js with
          // quantity:1 would silently shrink what the customer added.
          return cartChange({
            id: line ? line.key : parentLineKey,
            quantity: line ? line.quantity : 1,
            properties: buildParentProps(bundleId, desiredTitles)
          });
        });
      });
    }

    // Phase 1 — ADD any desired child not already present.
    seq = seq.then(function () {
      return fetchJSON('/cart.js').then(function (cart) {
        var childMap = buildBundleChildSets(cart);
        var current = childMap[bundleId] || {};
        var toAdd = desiredValues.filter(function (v) {
          return !Object.prototype.hasOwnProperty.call(current, v);
        });
        // Sequential adds (cart endpoints are not atomic across requests).
        return toAdd.reduce(function (p, v) {
          return p.then(function () {
            return cartAdd({
              items: [{
                id: parseInt(v, 10),
                quantity: 1,
                properties: {
                  _bundle_id: bundleId,
                  _bundle_role: 'child',
                  _bundle_parent_variant: String(parentVariant)
                }
              }]
            });
          });
        }, Promise.resolve());
      });
    });

    // Phase 2 — re-GET fresh keys, then REMOVE any current child not desired.
    seq = seq.then(function () {
      return fetchJSON('/cart.js').then(function (cart) {
        var childMap = buildBundleChildSets(cart);
        var current = childMap[bundleId] || {};
        var toRemove = Object.keys(current).filter(function (v) {
          return desiredValues.indexOf(v) === -1;
        });
        return toRemove.reduce(function (p, v) {
          return p.then(function () {
            return cartChange({ id: current[v], quantity: 0 });
          });
        }, Promise.resolve());
      });
    });

    // Phase 3 — re-GET fresh keys, then sync the parent's Includes props to
    // exactly the desired set (only meaningful when the parent already had a
    // bundleId; the no-bundleId case stamped Includes in Phase 0 but the set
    // may have changed via removals, so re-sync unconditionally for safety).
    seq = seq.then(function () {
      return fetchJSON('/cart.js').then(function (cart) {
        var line = findParentLine(cart, bundleId, parentLineKey);
        return cartChange({
          id: line ? line.key : parentLineKey,
          quantity: line ? line.quantity : 1,
          properties: buildParentProps(bundleId, desiredTitles)
        });
      });
    });

    seq.then(function () {
      // The DOM (checkboxes + optimistic summary) now equals what we persisted,
      // so there's NO need to re-render the section (which would reset scroll to
      // the top and flash). Keep the bundleId we may have just generated on the
      // stack so a coalesced re-commit doesn't re-stamp, and just refresh the
      // header count in place.
      if (!hadBundleId) stack.dataset.bundleId = bundleId;
      return fetchJSON('/cart.js').then(updateCount);
    }).then(function () {
      setStackLoading(stack, false);
      mutating = false;
      flushPending();
    }).catch(function (err) {
      // eslint-disable-next-line no-console
      console.error('[cart-drawer] commit failed', err);
      // eslint-disable-next-line no-alert
      window.alert('Could not update your cart. Please try again.');
      // Hard failure — resync checkbox state + summary from the live cart.
      reconcile().finally(function () {
        setStackLoading(stack, false);
        mutating = false;
        flushPending();
      });
    });
  }

  // Initial paint: populate the summary + apply the default accordion state from
  // the server-rendered DOM. openDrawer() re-reconciles from /cart.js on open,
  // so this is best-effort while the drawer is still closed.
  syncOpenStacksWithDom();
  applySoldOutState();
  recomputeSummary();
  setupStickyBar();
})();
